import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, MessageSquare } from "lucide-react";
import { formatDateTime } from "@/lib/date";
import { toast } from "sonner";

import { tx, lz } from "@/i18n";
function fmt(dt) {
  return formatDateTime(dt);
}

export function CommentThread({ entityType, entityId, onChange }) {
  const { user } = useAuth();
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = () =>
    api.get("/comments", { params: { entity_type: entityType, entity_id: entityId } })
      .then(({ data }) => { setComments(data); onChange?.(); })
      .catch(() => setComments([]));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType, entityId]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post("/comments", { entity_type: entityType, entity_id: entityId, text });
      setText("");
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSending(false); }
  };

  return (
    <div className="flex flex-col" data-testid="comment-thread">
      <div className="max-h-72 overflow-y-auto space-y-3 pr-1">
        {comments === null ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : comments.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-6">
            <MessageSquare className="h-7 w-7 mx-auto mb-2 opacity-40" /> {tx("Nog geen opmerkingen.")}
          </div>
        ) : (
          comments.map((c) => {
            const mine = c.author_id === user.id;
            return (
              <div key={c.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`comment-${c.id}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${mine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                  <div className={`text-[11px] mb-0.5 ${mine ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                    {c.author_name} · {ROLE_LABELS[c.author_role] || c.author_role} · {fmt(c.created_at)}
                  </div>
                  <div className="text-sm whitespace-pre-line leading-relaxed">{c.text}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="flex items-end gap-2 mt-3 border-t border-border pt-3">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={tx("Opmerking of bijzonderheid...")}
          rows={2}
          className="resize-none"
          data-testid="comment-input"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
        />
        <Button onClick={send} disabled={sending || !text.trim()} className="bg-primary hover:bg-primary/90 shrink-0" data-testid="comment-send-btn">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
